import React, { useEffect, useState, useRef } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import axios from "axios";
import { Toaster } from "./components/ui/sonner";
import ImpersonationBanner from "./components/ImpersonationBanner";
import { toast } from "sonner";
import InstallPWA from "./components/InstallPWA"; // eslint-disable-line no-unused-vars
import { PlanLimitProvider } from "./contexts/PlanLimitContext";
import { PushNotificationProvider } from "./contexts/PushNotificationContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import { TimezoneProvider } from "./contexts/TimezoneContext";
import { BalanceProvider } from "./contexts/BalanceContext";
import { VoiceProvider } from "./contexts/VoiceContext";
import DialerPanel, { DialerFAB, IncomingCallBanner } from "./components/voice/DialerPanel";
import { InAppCallProvider } from "./contexts/InAppCallContext";
import IncomingCallScreen from "./components/IncomingCallScreen";
import { canAccessPage } from "./utils/permissions";

// Pages
import LandingPage from "./pages/LandingPage";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import SupportPage from "./pages/SupportPage";
import DocsPage from "./pages/DocsPage";
import DocsGettingStartedPage from "./pages/DocsGettingStartedPage";
import DocsFeaturesPage from "./pages/DocsFeaturesPage";
import DocsTroubleshootingPage from "./pages/DocsTroubleshootingPage";
import DocsBillingPage from "./pages/DocsBillingPage";
import DocsMobilePage from "./pages/DocsMobilePage";
import ContactPage from "./pages/ContactPage";
import DashboardLayout from "./layouts/DashboardLayout";
import DashboardOverview from "./pages/dashboard/DashboardOverview";
import CustomersPage from "./pages/dashboard/CustomersPage";
import JobsPage from "./pages/dashboard/JobsPage";
import JobFormPage from "./pages/dashboard/JobFormPage";
import InvoicesPage from "./pages/dashboard/InvoicesPage";
import TeamPage from "./pages/dashboard/TeamPage";
import TechniciansPage from "./pages/dashboard/TechniciansPage";
import TeamMapPage from "./pages/dashboard/TeamMapPage";
import AnalyticsPage from "./pages/dashboard/AnalyticsPage";
import SettingsPage from "./pages/dashboard/SettingsPage";
import MessagesPage from "./pages/dashboard/MessagesPage";
import CallLogsPage from "./pages/dashboard/CallLogsPage";
import FinancialsPage from "./pages/dashboard/FinancialsPage";
import ExpensesPage from "./pages/dashboard/ExpensesPage";
import AutomationsPage from "./pages/dashboard/AutomationsPage";
import CampaignsPage from "./pages/dashboard/CampaignsPage";
import InventoryPage from "./pages/dashboard/InventoryPage";
import AdminDashboard from "./pages/admin/AdminDashboard";

// Technician Portal Pages
import TechLogin from "./pages/tech/TechLogin";
import TechSetup from "./pages/tech/TechSetup";
import TechProfile from "./pages/tech/TechProfile";
import TechLayout from "./pages/tech/TechLayout";
import TechDashboard from "./pages/tech/TechDashboard";
import TechJobs from "./pages/tech/TechJobs";
import TechFinancials from "./pages/tech/TechFinancials";
import TechStock from "./pages/tech/TechStock";
import TechDocuments from "./pages/tech/TechDocuments";

// Resource Pages
import VideoTutorialsPage from "./pages/VideoTutorialsPage";
import ReleaseNotesPage from "./pages/ReleaseNotesPage";
import QuickStartPage from "./pages/QuickStartPage";

// Public Pages
import JobApproval from "./pages/JobApproval";
import TeamSetup from "./pages/TeamSetup";
import PrivacyPolicyPage from "./pages/PrivacyPolicyPage";
import TermsOfServicePage from "./pages/TermsOfServicePage";
import PricingPage from "./pages/PricingPage";
import BookDemoPage from "./pages/BookDemoPage";
import AccountDeletePage from "./pages/AccountDeletePage";
import PublicLeadForm from "./pages/PublicLeadForm";
import PublicEstimateView from "./pages/PublicEstimateView";
import PublicProposalView from "./pages/PublicProposalView";

// Marketing SEO Pages (Phases 2-4)
import FeaturesIndexPage from "./pages/marketing/FeaturesIndexPage";
import FeaturePage from "./pages/marketing/FeaturePage";
import IndustriesIndexPage from "./pages/marketing/IndustriesIndexPage";
import IndustryPage from "./pages/marketing/IndustryPage";
import MultiLocationPage from "./pages/marketing/MultiLocationPage";
import BlogIndexPage from "./pages/marketing/BlogIndexPage";
import BlogPostPage from "./pages/marketing/BlogPostPage";
import CompareIndexPage from "./pages/marketing/CompareIndexPage";
import ComparePage from "./pages/marketing/ComparePage";

// Customer Portal Pages
import PortalLayout from "./pages/portal/PortalLayout";
import PortalLogin from "./pages/portal/PortalLogin";
import PortalRegister from "./pages/portal/PortalRegister";
import PortalDashboard from "./pages/portal/PortalDashboard";
import { PortalJobs, PortalJobDetails } from "./pages/portal/PortalJobs";
import { PortalInvoices, PortalInvoiceDetails } from "./pages/portal/PortalInvoices";
import PortalAddresses from "./pages/portal/PortalAddresses";
import PortalSettings from "./pages/portal/PortalSettings";
import PortalPayments from "./pages/portal/PortalPayments";
import PublicInvoicePay from "./pages/public/PublicInvoicePay";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Install silent-refresh interceptors before anything else fires API calls.
import { installAuthInterceptors, setOnSessionEnded, tokenStorage, techTokenStorage } from "./utils/authTokenManager";
installAuthInterceptors();


// Global 402 interceptor: Show "Add Funds" toast for insufficient balance (web only)
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 402) {
      const isMobile = !!window.FieldzNative?.isNativeApp;
      const detail = error.response.data?.detail;
      const msg = typeof detail === "object" ? detail.message : detail;
      const balance = typeof detail === "object" ? detail.balance : undefined;
      import("sonner").then(({ toast }) => {
        if (isMobile) {
          toast.error(msg || "Service limit reached", {
            description: "Visit fieldzai.com to manage your subscription and add funds.",
            duration: 8000,
          });
        } else {
          toast.error(msg || "Insufficient funds", {
            description: balance !== undefined
              ? `Current balance: $${balance.toFixed(2)}. Add funds to continue.`
              : "Add funds to your account to continue using this service.",
            action: {
              label: "Add Funds",
              onClick: () => window.location.href = "/dashboard/settings?tab=subscription",
            },
            duration: 8000,
          });
        }
      });
    }
    return Promise.reject(error);
  }
);

// Auth Context
export const AuthContext = React.createContext(null);

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const AuthCallback = () => {
  const navigate = useNavigate();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      const hash = window.location.hash;
      const sessionIdMatch = hash.match(/session_id=([^&]+)/);
      
      if (sessionIdMatch) {
        const sessionId = sessionIdMatch[1];
        try {
          const response = await axios.post(
            `${API}/auth/session`,
            { session_id: sessionId }
          );
          
          const userData = { ...response.data };
          const token = userData.token;
          const refreshToken = userData.refresh_token;
          delete userData.token;
          delete userData.refresh_token;

          // Role-aware redirect for Google OAuth
          if (userData.role === "technician") {
            techTokenStorage.setPair({ token, refresh_token: refreshToken });
            localStorage.setItem("tech_user", JSON.stringify({
              member_id: userData.user_id,
              name: userData.name,
              email: userData.email,
              role: "technician",
              business_user_id: userData.owner_id || "",
            }));
            tokenStorage.setAccess(null);
            localStorage.removeItem("user");
            window.location.href = "/tech/dashboard";
          } else {
            tokenStorage.setPair({ token, refresh_token: refreshToken });
            localStorage.setItem("user", JSON.stringify(userData));
            localStorage.removeItem("tech_token");
            localStorage.removeItem("tech_refresh_token");
            localStorage.removeItem("tech_user");
            window.location.href = "/dashboard";
          }
        } catch (error) {
          console.error("Auth error:", error);
          navigate("/login");
        }
      } else {
        navigate("/login");
      }
    };

    processAuth();
  }, [navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#020817]">
      <div className="text-white text-xl">Authenticating...</div>
    </div>
  );
};

const ProtectedRoute = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  const [user, setUser] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const checkAuth = async () => {
      const storedUser = localStorage.getItem("user");
      const token = localStorage.getItem("token");
      const techUser = localStorage.getItem("tech_user");
      const techToken = localStorage.getItem("tech_token");

      // If a tech session exists but NO owner session, redirect to tech portal
      if (techUser && techToken && !token && !storedUser) {
        navigate("/tech/dashboard", { replace: true });
        return;
      }

      // If BOTH sessions exist (stale data), the owner session takes priority
      // Clean up stale tech session keys
      if (techUser && techToken && token && storedUser) {
        localStorage.removeItem("tech_token");
        localStorage.removeItem("tech_user");
      }

      // Check if stored user is a technician (logged in via main login)
      if (storedUser) {
        try {
          const parsed = JSON.parse(storedUser);
          if (parsed.role === "technician") {
            // Migrate to tech session and redirect
            if (token) {
              localStorage.setItem("tech_token", token);
              localStorage.setItem("tech_user", JSON.stringify({
                member_id: parsed.user_id,
                name: parsed.name,
                email: parsed.email,
                role: "technician",
                business_user_id: parsed.owner_id || "",
              }));
              localStorage.setItem("auth_kind", "tech");
            }
            localStorage.removeItem("token");
            localStorage.removeItem("user");
            navigate("/tech/dashboard", { replace: true });
            return;
          }
        } catch { /* invalid JSON, proceed normally */ }
      }

      // Use stored user for initial render but always refresh from API
      if (storedUser) {
        setUser(JSON.parse(storedUser));
        setIsAuthenticated(true);
      }

      // Always check with backend to get fresh user data (including is_admin)
      try {
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const response = await axios.get(`${API}/auth/me`, {
          headers
        });

        // If backend says this is a technician, redirect
        if (response.data.role === "technician") {
          localStorage.setItem("tech_token", token);
          localStorage.setItem("tech_user", JSON.stringify({
            member_id: response.data.user_id,
            name: response.data.name,
            email: response.data.email,
            role: "technician",
          }));
          localStorage.setItem("auth_kind", "tech");
          localStorage.removeItem("token");
          localStorage.removeItem("user");
          navigate("/tech/dashboard", { replace: true });
          return;
        }

        setUser(response.data);
        setIsAuthenticated(true);
        localStorage.setItem("user", JSON.stringify(response.data));
      } catch (error) {
        // If no stored user and API fails, redirect to login
        if (!storedUser) {
          setIsAuthenticated(false);
          localStorage.removeItem("user");
          localStorage.removeItem("token");
          navigate("/login", { state: { from: location.pathname } });
        }
      }
    };

    checkAuth();
  }, [navigate, location.pathname]);

  if (isAuthenticated === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#020817]">
        <div className="text-white text-xl">Loading...</div>
      </div>
    );
  }

  return isAuthenticated ? (
    <AuthContext.Provider value={{ user, setUser }}>
      <PlanLimitProvider>
        <BalanceProvider>
          <PushNotificationProvider user={user}>
            <VoiceProvider>
              {children}
            </VoiceProvider>
          </PushNotificationProvider>
        </BalanceProvider>
      </PlanLimitProvider>
    </AuthContext.Provider>
  ) : null;
};

// Role-based route protection wrapper
const RoleProtectedRoute = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [checked, setChecked] = useState(false);
  
  useEffect(() => {
    const storedUser = localStorage.getItem("user");
    if (storedUser) {
      const user = JSON.parse(storedUser);
      // Technicians should never be in the dashboard shell
      if (user.role === "technician") {
        navigate("/tech/dashboard", { replace: true });
        return;
      }
      if (!canAccessPage(user, location.pathname)) {
        toast.error("You don't have permission to access this page");
        navigate("/dashboard", { replace: true });
        return;
      }
    }
    setChecked(true);
  }, [location.pathname, navigate]);
  
  if (!checked) return null;
  return children;
};

// Dialer wrapper for authenticated dashboard routes
const DialerWrapper = ({ children }) => {
  const [dialerOpen, setDialerOpen] = React.useState(false);
  const loc = useLocation();
  const showDialer = loc.pathname === "/dashboard" || loc.pathname === "/dashboard/";
  return (
    <InAppCallProvider>
      {children}
      <IncomingCallBanner />
      <IncomingCallScreen />
      {showDialer && <DialerFAB onClick={() => setDialerOpen(true)} />}
      <DialerPanel isOpen={dialerOpen} onClose={() => setDialerOpen(false)} />
    </InAppCallProvider>
  );
};

function AppRouter() {
  const location = useLocation();
  const navigate = useNavigate();

  // Hook the silent-refresh interceptor into React Router so a hard logout
  // performs a clean SPA navigation instead of a full page reload.
  useEffect(() => {
    setOnSessionEnded(() => {
      const onTechRoute = window.location.pathname.startsWith("/tech");
      navigate(onTechRoute ? "/tech/login" : "/login", { replace: true });
    });
    return () => setOnSessionEnded(null);
  }, [navigate]);

  // Check for session_id in URL hash - handle auth callback
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/features" element={<FeaturesIndexPage />} />
      <Route path="/features/:slug" element={<FeaturePage />} />
      <Route path="/industries" element={<IndustriesIndexPage />} />
      <Route path="/industries/:slug" element={<IndustryPage />} />
      <Route path="/multi-location" element={<MultiLocationPage />} />
      <Route path="/blog" element={<BlogIndexPage />} />
      <Route path="/blog/:slug" element={<BlogPostPage />} />
      <Route path="/compare" element={<CompareIndexPage />} />
      <Route path="/compare/:slug" element={<ComparePage />} />
      <Route path="/book-demo" element={<BookDemoPage />} />
      <Route path="/support" element={<SupportPage />} />
      <Route path="/docs" element={<DocsPage />} />
      <Route path="/docs/getting-started" element={<DocsGettingStartedPage />} />
      <Route path="/docs/features" element={<DocsFeaturesPage />} />
      <Route path="/docs/troubleshooting" element={<DocsTroubleshootingPage />} />
      <Route path="/docs/billing" element={<DocsBillingPage />} />
      <Route path="/docs/mobile" element={<DocsMobilePage />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/tutorials" element={<VideoTutorialsPage />} />
      <Route path="/release-notes" element={<ReleaseNotesPage />} />
      <Route path="/quick-start" element={<QuickStartPage />} />
      <Route path="/privacy" element={<PrivacyPolicyPage />} />
      <Route path="/terms" element={<TermsOfServicePage />} />
      <Route path="/account/delete" element={<AccountDeletePage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      
      {/* Public Job Approval Page */}
      <Route path="/job-approval/:token" element={<JobApproval />} />
      
      {/* Customer Portal Routes */}
      <Route path="/portal/login" element={<PortalLogin />} />
      <Route path="/form/:formId" element={<PublicLeadForm />} />
      <Route path="/estimate/:token" element={<PublicEstimateView />} />
      <Route path="/invoice/:token" element={<PublicEstimateView />} />
      <Route path="/proposal/:token" element={<PublicProposalView />} />
      <Route path="/portal/register" element={<PortalRegister />} />
      <Route path="/portal" element={<PortalLayout />}>
        <Route index element={<PortalDashboard />} />
        <Route path="jobs" element={<PortalJobs />} />
        <Route path="jobs/:jobId" element={<PortalJobDetails />} />
        <Route path="invoices" element={<PortalInvoices />} />
        <Route path="invoices/:invoiceId" element={<PortalInvoiceDetails />} />
        <Route path="payments" element={<PortalPayments />} />
        <Route path="addresses" element={<PortalAddresses />} />
        <Route path="settings" element={<PortalSettings />} />
      </Route>

      {/* Public Guest Invoice Payment Page — no login required */}
      <Route path="/pay/:token" element={<PublicInvoicePay />} />
      
      {/* Technician Portal Routes */}
      <Route path="/tech/login" element={<TechLogin />} />
      <Route path="/tech/setup/:token" element={<TechSetup />} />
      <Route path="/team/setup/:token" element={<TeamSetup />} />
      <Route path="/tech" element={<TimezoneProvider><TechLayout /></TimezoneProvider>}>
        <Route index element={<Navigate to="/tech/dashboard" replace />} />
        <Route path="dashboard" element={<TechDashboard />} />
        <Route path="jobs" element={<TechJobs />} />
        <Route path="documents" element={<TechDocuments />} />
        <Route path="stock" element={<TechStock />} />
        <Route path="financials" element={<TechFinancials />} />
        <Route path="profile" element={<TechProfile />} />
        {/* Mobile app's "Profile" menu historically pointed at /tech/setup
            (token-less) which fell into the catch-all → login page. */}
        <Route path="setup" element={<Navigate to="/tech/profile" replace />} />
      </Route>
      
      {/* Owner Dashboard Routes */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <TimezoneProvider>
              <DialerWrapper>
                <DashboardLayout />
              </DialerWrapper>
            </TimezoneProvider>
          </ProtectedRoute>
        }
      >
        <Route index element={<DashboardOverview />} />
        <Route path="customers" element={<CustomersPage />} />
        <Route path="jobs" element={<JobsPage />} />
        <Route path="jobs/new" element={<JobFormPage />} />
        <Route path="jobs/:jobId/edit" element={<JobFormPage />} />
        <Route path="invoices" element={<InvoicesPage />} />
        <Route path="team" element={<RoleProtectedRoute><TeamPage /></RoleProtectedRoute>} />
        <Route path="technicians" element={<TechniciansPage />} />
        <Route path="team-map" element={<TeamMapPage />} />
        <Route path="messages" element={<MessagesPage />} />
        <Route path="calls" element={<CallLogsPage />} />
        <Route path="analytics" element={<RoleProtectedRoute><AnalyticsPage /></RoleProtectedRoute>} />
        <Route path="financials" element={<RoleProtectedRoute><FinancialsPage /></RoleProtectedRoute>} />
        <Route path="expenses" element={<RoleProtectedRoute><ExpensesPage /></RoleProtectedRoute>} />
        <Route path="automations" element={<RoleProtectedRoute><AutomationsPage /></RoleProtectedRoute>} />
        <Route path="campaigns" element={<RoleProtectedRoute><CampaignsPage /></RoleProtectedRoute>} />
        <Route path="inventory" element={<RoleProtectedRoute><InventoryPage /></RoleProtectedRoute>} />
        <Route path="settings" element={<RoleProtectedRoute><SettingsPage /></RoleProtectedRoute>} />
      </Route>
      <Route
        path="/admin/*"
        element={
          <ProtectedRoute>
            <AdminDashboard />
          </ProtectedRoute>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <HelmetProvider>
      <ThemeProvider>
        <BrowserRouter>
          <ImpersonationBanner />
          <AppRouter />
          <Toaster position="top-right" />
        </BrowserRouter>
      </ThemeProvider>
    </HelmetProvider>
  );
}

export default App;
